import { getFormatter, getPercentOf, InstrumentCategoryEnum, ListedInstrument } from '@eih-netbroker/common';
import { useMemo } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { InstrumentCategoryLogo } from '../../../../shared/components/instrument-category-logo/InstrumentCategoryLogo';
import { InstumentMarketPriceDisplay } from '../../../../shared/components/instrument-market-price-display/InstrumentMarketPriceDisplay';
import { NbTooltip } from '../../../../shared/components/tooltip/Tooltip';
import { ProductDetailsBase } from '../../states/productDetailsStateAtom';
import classes from './ProductDetailsHeader.module.scss';
import { ProductDetailsListRow } from './ProductDetailsListRow';

interface ProductDetailsHeaderProps {
  className?: string;
  product: ProductDetailsBase;
  instrument: ListedInstrument;
}

export const ProductDetailsHeader = ({ className, product, instrument }: ProductDetailsHeaderProps) => {
  const { translation } = useLocale();
  const { formatPercentage, formatNumberF2 } = getFormatter();
  const lastPricePosition = useMemo(() => {
    if (
      !product.instrumentStoreItem.price?.value ||
      !product.instrumentStoreItem.details?.lowDaily ||
      !product.instrumentStoreItem.details?.highDaily
    ) {
      return;
    }
    const percent = getPercentOf(
      product.instrumentStoreItem.price?.value - product.instrumentStoreItem.details?.lowDaily,
      product.instrumentStoreItem.details.highDaily - product.instrumentStoreItem.details?.lowDaily,
    );

    if (percent >= 100) {
      return '100%';
    }

    return percent + '%';
  }, [product]);

  const averagePricePosition = useMemo(() => {
    if (
      !product.instrumentStoreItem.details?.averagePrice ||
      !product.instrumentStoreItem.details?.lowDaily ||
      !product.instrumentStoreItem.details?.highDaily
    ) {
      return;
    }

    const percent = getPercentOf(
      product.instrumentStoreItem.details?.averagePrice - product.instrumentStoreItem.details.lowDaily,
      product.instrumentStoreItem.details.highDaily - product.instrumentStoreItem.details.lowDaily,
    );

    return percent + '%';
  }, [product]);

  return (
    <div className={`${classes.header} ${className}`}>
      <div className={classes.headerTitleRow}>
        <InstrumentCategoryLogo
          className={classes.headerTitleRowCategory}
          instrumentCategory={instrument.instrumentCategory as InstrumentCategoryEnum}
        />
        <span className={classes.headerTitleRowText}>{instrument.ticker}</span>
      </div>
      <div className={classes.headerContent}>
        <InstumentMarketPriceDisplay
          instrument={instrument}
          withCurrency={false}
          price={product.instrumentStoreItem.price}
          priceClassName={classes.headerContentLastPrice}
        />
        <div className={classes.headerContentColumn}>
          <span
            className={`${classes.headerContentChangeText} ${
              product.instrumentStoreItem.details?.changeNominal !== undefined &&
              product.instrumentStoreItem.details?.changeNominal < 0
                ? classes.headerContentChangeTextIsNegative
                : classes.headerContentChangeTextIsPositive
            }`}
          >
            {product.instrumentStoreItem.details?.changeNominal
              ? formatNumberF2(product.instrumentStoreItem.details.changeNominal)
              : ''}
          </span>
          <span
            className={`${classes.headerContentChangeText} ${
              product.instrumentStoreItem.details?.changePercentage &&
              product.instrumentStoreItem.details?.changePercentage < 0
                ? classes.headerContentChangeTextIsNegative
                : classes.headerContentChangeTextIsPositive
            }`}
          >
            {product.instrumentStoreItem.details?.changePercentage
              ? formatPercentage(product.instrumentStoreItem.details.changePercentage)
              : ''}
          </span>
        </div>
        <div className={classes.headerContentColumn}>
          <ProductDetailsListRow
            label={translation.productDetails.lowShort}
            value={product.low ? product.low : '-'}
            isNumber
          />
          <ProductDetailsListRow
            label={translation.productDetails.averagePrice}
            value={product.averagePrice}
            info={translation.productDetails.averagePriceInfo}
            isNumber
          />
          <ProductDetailsListRow
            label={translation.productDetails.highShort}
            value={product.high}
            isNumber
          />
        </div>
      </div>
      <div className={classes.bar}>
        <div className={`${classes.barInterval} ${classes.barIntervalIsStart}`}>
          <div className={classes.barIntervalLine} />
          <span>{translation.productDetails.min}</span>
        </div>
        <div className={`${classes.barInterval} ${classes.barIntervalIsEnd}`}>
          <div className={classes.barIntervalLine} />
          <span>{translation.productDetails.max}</span>
        </div>
        <NbTooltip
          title={`${translation.productDetails.lastPrice} ${product.lastPrice}`}
          placement="top"
        >
          <div
            className={`${classes.barCircle} ${
              product.instrumentStoreItem.raw?.PRCTCK_1 === '⇧'
                ? classes.barCircleIsGreen
                : product.instrumentStoreItem.raw?.PRCTCK_1 === '⇩'
                ? classes.barCircleIsRed
                : classes.barCircleIsOrange
            } ${product.newHighOrLowFlag ? classes.barCircleIsAtLimit : ''} ${
              lastPricePosition === '100%' ? classes.barCircleIsMax : ''
            }`}
            style={{ left: lastPricePosition }}
          >
            <span />
            <span />
          </div>
        </NbTooltip>

        <NbTooltip
          title={`${translation.productDetails.averagePrice} ${product.averagePrice}`}
          placement="top"
        >
          <div
            className={`${classes.barCircle} ${classes.barCircleIsBlue}`}
            style={{ left: averagePricePosition }}
          />
        </NbTooltip>
      </div>
    </div>
  );
};
